class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    if (this.notification) {
      this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    }

    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    if (!this.notification) return;

    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);
  }

  close() {
    if (!this.notification) return;

    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  async renderContents(parsedState) {
    this.cartItemKey = parsedState.key;

    const addedItems = await this.resolveAddedItems(parsedState);

    if (addedItems.length > 0) {
      await this.renderCustomContents(parsedState, addedItems);
    } else {
      this.renderFallbackContents(parsedState);
    }

    if (this.header) this.header.reveal();
    this.open();
  }

  renderFallbackContents(parsedState) {
    this.getSectionsToRender().forEach((section) => {
      const target = document.getElementById(section.id);
      if (!target || !parsedState.sections || !parsedState.sections[section.id]) return;

      const html = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (html) target.innerHTML = html;
    });
  }

  async resolveAddedItems(parsedState) {
    let addedItems = Array.isArray(parsedState.items) ? parsedState.items : [];
    let cartItems = [];

    try {
      const cartResponse = await fetch(`${window.routes.cart_url}.js`, {
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      const cartData = await cartResponse.json();
      cartItems = Array.isArray(cartData.items) ? cartData.items : [];
    } catch (error) {
      console.error('Could not fetch cart.js for notification', error);
    }

    // 1) Standardfall: servern returnerar items[] för alla nyss tillagda artiklar
    if (addedItems.length > 0 && cartItems.length > 0) {
      const enriched = [];

      addedItems.forEach((addedItem) => {
        const match =
          cartItems.find((cartItem) => addedItem.key && cartItem.key === addedItem.key) ||
          cartItems.find((cartItem) => String(cartItem.variant_id) === String(addedItem.variant_id)) ||
          cartItems.find((cartItem) => String(cartItem.id) === String(addedItem.id));

        if (match) enriched.push(match);
      });

      if (enriched.length > 0) {
        return this.uniqueItemsByKey(enriched);
      }
    }

    // 2) Fallback: om parsedState bara innehåller en huvudprodukt men cart count ökade mer,
    // visa de senaste artiklarna i varukorgen först.
    if (cartItems.length > 0) {
      const latestItems = [...cartItems]
        .sort((a, b) => {
          // senaste först baserat på line/position i cart
          const aIndex = typeof a.line === 'number' ? a.line : 0;
          const bIndex = typeof b.line === 'number' ? b.line : 0;
          return bIndex - aIndex;
        })
        .slice(0, 4);

      return this.uniqueItemsByKey(latestItems);
    }

    return [];
  }

  uniqueItemsByKey(items) {
    const seen = new Set();
    return items.filter((item) => {
      const key = item.key || `${item.id}-${item.variant_id || ''}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  async renderCustomContents(parsedState, addedItems) {
    const productContainer = document.getElementById('cart-notification-product');
    const buttonContainer = document.getElementById('cart-notification-button');
    const cartBubble = document.getElementById('cart-icon-bubble');

    if (productContainer) {
      productContainer.innerHTML = `
        <div class="cart-notification-product__multi">
          ${addedItems.map((item) => this.buildItemMarkup(item)).join('')}
        </div>
      `;
    }

    if (buttonContainer && parsedState.sections && parsedState.sections['cart-notification-button']) {
      const buttonHtml = this.getSectionInnerHTML(parsedState.sections['cart-notification-button']);
      if (buttonHtml) buttonContainer.innerHTML = buttonHtml;
    }

    if (cartBubble && parsedState.sections && parsedState.sections['cart-icon-bubble']) {
      const bubbleHtml = this.getSectionInnerHTML(parsedState.sections['cart-icon-bubble']);
      if (bubbleHtml) cartBubble.innerHTML = bubbleHtml;
    }
  }

  buildItemMarkup(item) {
    const imageUrl = this.getItemImage(item);
    const productUrl = this.getItemUrl(item);
    const title = this.escapeHtml(item.product_title || item.title || '');
    const variantText = this.getVariantText(item);
    const quantity = Number(item.quantity || 1);
    const priceHtml = this.getPriceMarkup(item);

    return `
      <div class="cn-product-row">
        ${
          imageUrl
            ? `
              <div class="cn-product-row__image">
                ${productUrl ? `<a href="${productUrl}" tabindex="-1" aria-hidden="true">` : ''}
                  <img
                    src="${imageUrl}"
                    alt="${title}"
                    loading="lazy"
                  >
                ${productUrl ? `</a>` : ''}
              </div>
            `
            : ''
        }

        <div class="cn-product-row__info">
          <div class="cn-product-row__title">
            ${
              productUrl
                ? `<a href="${productUrl}" style="color:inherit; text-decoration:none;">${title}</a>`
                : title
            }
          </div>

          ${variantText ? `<div class="cn-product-row__variant">${variantText}</div>` : ''}
          ${quantity > 1 ? `<div class="cn-product-row__variant">Antal: ${quantity}</div>` : ''}
        </div>

        <div class="cn-product-row__price">
          ${priceHtml}
        </div>
      </div>
    `;
  }

  getPriceMarkup(item) {
    const finalCents =
      typeof item.final_line_price === 'number'
        ? item.final_line_price
        : typeof item.final_price === 'number'
          ? item.final_price
          : typeof item.line_price === 'number'
            ? item.line_price
            : typeof item.price === 'number'
              ? item.price
              : null;

    const originalCents =
      typeof item.original_line_price === 'number'
        ? item.original_line_price
        : typeof item.original_price === 'number'
          ? item.original_price
          : finalCents;

    if (finalCents == null) return '';

    const finalMoney = this.formatMoney(finalCents);
    const originalMoney = this.formatMoney(originalCents);
    const isDiscounted = typeof originalCents === 'number' && originalCents > finalCents;

    if (isDiscounted) {
      return `
        <div class="cn-product-row__price-current">${finalMoney}</div>
        <div class="cn-product-row__price-original">${originalMoney}</div>
      `;
    }

    return `
      <div class="cn-product-row__price-current">${finalMoney}</div>
    `;
  }

  formatMoney(cents) {
    if (typeof Shopify !== 'undefined' && typeof Shopify.formatMoney === 'function') {
      return Shopify.formatMoney(cents);
    }

    const amount = (Number(cents) / 100).toLocaleString('sv-SE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return `${amount} kr`;
  }

  getItemImage(item) {
    if (item.image) {
      if (typeof item.image === 'string') return item.image;
      if (item.image.url) return item.image.url;
      if (item.image.src) return item.image.src;
    }

    if (item.featured_image) {
      if (typeof item.featured_image === 'string') return item.featured_image;
      if (item.featured_image.url) return item.featured_image.url;
      if (item.featured_image.src) return item.featured_image.src;
    }

    if (item.featured_media?.preview_image?.src) {
      return item.featured_media.preview_image.src;
    }

    return '';
  }

  getItemUrl(item) {
    return item.url || item.product_url || '';
  }

  getVariantText(item) {
    if (Array.isArray(item.options_with_values) && item.options_with_values.length > 0) {
      const filtered = item.options_with_values.filter((option) => {
        const name = String(option.name || '').trim().toLowerCase();
        const value = String(option.value || '').trim().toLowerCase();

        if (!value) return false;
        if (value === 'default title') return false;
        if (name === 'title' && value === 'default title') return false;

        return true;
      });

      if (filtered.length > 0) {
        return filtered.map((option) => this.escapeHtml(option.value)).join(', ');
      }
    }

    if (Array.isArray(item.options) && item.options.length > 0) {
      const filtered = item.options.filter((value) => {
        const normalized = String(value || '').trim().toLowerCase();
        return normalized && normalized !== 'default title';
      });

      if (filtered.length > 0) {
        return filtered.map((value) => this.escapeHtml(value)).join(', ');
      }
    }

    if (item.variant_title) {
      const variantTitle = String(item.variant_title).trim();
      if (variantTitle && variantTitle.toLowerCase() !== 'default title') {
        return this.escapeHtml(variantTitle);
      }
    }

    return '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const element = parsed.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}

customElements.define('cart-notification', CartNotification);